import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import LanguageSwitcher from '../../components/Navigation/LanguageSwitcher';
import ForgotPasswordModal from '../../components/Auth/ForgotPasswordModal';


const Login = () => {
  const { t, safeT } = useTranslationReady();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [loginResult, setLoginResult] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('');
  const [accessUrl, setAccessUrl] = useState('');
  const [lastLoginAttempt, setLastLoginAttempt] = useState(0);

  // Load saved username on component mount
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      setFormData(prev => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
    }
  }, []);
  const { login, ensureTenantContext } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.location) {
        setAccessUrl(window.location.origin);
      }
    } catch (_) {
      // noop
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error messages when user starts typing
    if (errorMessage) {
      setErrorMessage('');
      setErrorType('');
    }

    // If username is changed and it's different from saved username, uncheck remember me
    if (name === 'username') {
      const savedUsername = localStorage.getItem('rememberedUsername');
      if (savedUsername && value !== savedUsername) {
        setRememberMe(false);
      } else if (savedUsername && value === savedUsername) {
        setRememberMe(true);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple simultaneous login attempts
    if (loading) {
      return;
    }
    
    // Prevent rapid successive login attempts (minimum 1 second between attempts)
    const now = Date.now();
    const timeSinceLastAttempt = now - lastLoginAttempt;
    if (timeSinceLastAttempt < 1000) {
      setErrorMessage('Please wait a moment before trying again.');
      setErrorType('warning');
      return;
    }
    setLastLoginAttempt(now);
    
    setLoading(true);
    setErrorMessage('');
    setErrorType('');
    
    try {
      const result = await login(formData);
      
      if (result.success) {
        // Handle remember me functionality
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', formData.username);
        } else {
          localStorage.removeItem('rememberedUsername');
        }
        
        setLoginResult(result);
        
        // Handle different user types and repository requirements
        if (result.userType === 'admin') {
          // Admin users go directly to admin dashboard
          navigate('/admin');
        } else if (result.userType === 'regular') {
          // Regular users
          if (result.requiresTenantSelection && result.tenants?.length > 1) {
            // Multiple tenants - redirect to tenant selection page
            navigate('/select-tenant', { state: { tenants: result.tenants } });
          } else if (result.tenants?.length === 1) {
            // Single tenant - go directly to dashboard
            await ensureTenantContext();
            navigate('/dashboard');
          } else if (result.defaultTenant) {
            // Has default tenant - go to dashboard
            await ensureTenantContext();
            navigate('/dashboard');
          } else if (result.tenants?.length > 0) {
            // Has tenants but no default - redirect to selection page
            navigate('/select-tenant', { state: { tenants: result.tenants } });
          } else {
            // No tenants - this shouldn't happen with proper setup, but handle gracefully
            navigate('/dashboard');
          }
        }
      } else {
        // Handle login failure with specific error messages
        handleLoginError(result);
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different types of errors more gracefully
      let errorMessage = 'Login failed. Please try again.';
      let errorType = 'error';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        errorType = 'warning';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      handleLoginError({ 
        error: errorMessage,
        type: errorType
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoginError = (result) => {
    let message = '';
    let type = 'error';
    
    if (result.error) {
      // Handle specific error codes from backend
      switch (result.code) {
        case 'INVALID_CREDENTIALS':
          message = safeT('auth.errors.invalidCredentials', 'Invalid username or password. Please check your credentials and try again.');
          break;
        case 'ACCOUNT_LOCKED':
          message = result.message || safeT('auth.errors.accountLocked', 'Your account is temporarily locked due to multiple failed login attempts. Please try again later or contact your administrator.');
          type = 'warning';
          break;
        case 'RATE_LIMIT_EXCEEDED':
          // Handle rate limit error specifically
          const retryMinutes = result.details?.retryAfter ? Math.ceil(result.details.retryAfter / 60) : 15;
          message = safeT('auth.errors.rateLimitExceeded', 
            `Too many login attempts. Please wait ${retryMinutes} minutes before trying again. This is a security measure to protect your account.`
          );
          type = 'warning';
          break;
        case 'USER_NOT_FOUND':
          message = safeT('auth.errors.userNotFound', 'No account found with this username or email. Please check your credentials or contact your administrator.');
          break;
        case 'LOGIN_ERROR':
          message = safeT('auth.errors.loginError', 'A system error occurred during login. Please try again or contact support if the problem persists.');
          break;
        case 'NETWORK_ERROR':
          message = safeT('auth.errors.networkError', 'Unable to connect to the server. Please check your internet connection and try again.');
          break;
        default:
          // Handle generic error messages
          if (result.error.includes('401') || result.error.includes('Unauthorized')) {
            message = safeT('auth.errors.unauthorized', 'Invalid username or password. Please check your credentials and try again.');
          } else if (result.error.includes('403') || result.error.includes('Forbidden')) {
            message = safeT('auth.errors.forbidden', 'Access denied. Your account may not have the required permissions.');
            type = 'warning';
          } else if (result.error.includes('429') || result.error.includes('Too many')) {
            message = safeT('auth.errors.rateLimitExceeded', 
              'Too many login attempts. Please wait 15 minutes before trying again. This is a security measure to protect your account.'
            );
            type = 'warning';
          } else if (result.error.includes('500') || result.error.includes('Internal Server Error')) {
            message = safeT('auth.errors.serverError', 'A server error occurred. Please try again later or contact support.');
          } else if (result.error.includes('Network Error') || result.error.includes('timeout')) {
            message = safeT('auth.errors.networkError', 'Unable to connect to the server. Please check your internet connection and try again.');
          } else {
            message = result.error;
          }
      }
    } else {
      message = safeT('auth.errors.generic', 'Login failed. Please try again.');
    }
    
    setErrorMessage(message);
    setErrorType(type);
  };



  const handleForgotPassword = (e) => {
    e.preventDefault();
    setShowForgotPasswordModal(true);
  };

  const handleEmailSent = (email) => {
    setShowForgotPasswordModal(false);
    // Optionally show a success message
    setErrorMessage(safeT('auth.forgotPasswordFlow.emailSentSuccess', `Password reset email sent to ${email}`));
    setErrorType('success');
  };



  return (
    <div className="backdrop-blur-md bg-gray-900/95 p-8 rounded-xl shadow-2xl w-full max-w-md border border-gray-700/50">
      {/* Header with Language Switcher */}
      <div className="ml-4">
          <LanguageSwitcher />
        </div>

      <div className="mb-8">
        <div className="text-xl font-semibold text-white leading-tight">Welcome to</div>
        <div className="text-3xl font-extrabold text-white whitespace-nowrap w-full">Flow Agentic Sales</div>
        <div className="mt-3 flex justify-center">
          <img src="/cmr_logo.png" alt={t('auth.crmLogo')} className="h-12 w-12 object-contain" />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.username')} / {t('auth.email')}
          </label>
          <p className="text-xs text-gray-400 mb-2">
            {t('auth.loginWithUsernameOrEmail')}
          </p>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              className="appearance-none block w-full px-3 py-3 pl-10 border border-gray-600 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm bg-gray-800/80 backdrop-blur-sm transition-all duration-200 text-white"
              placeholder={t('auth.username')}
              value={formData.username}
              onChange={handleChange}
            />
          </div>
          <p className="mt-2 text-sm text-gray-400">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="font-medium text-teal-400 hover:text-teal-300">
              {t('auth.register')}
            </Link>
          </p>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.password')}
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              className="appearance-none block w-full px-3 py-3 pl-10 pr-10 border border-gray-600 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm bg-gray-800/80 backdrop-blur-sm transition-all duration-200 text-white"
              placeholder={t('auth.password')}
              value={formData.password}
              onChange={handleChange}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-300 transition-colors duration-200"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-teal-500 focus:ring-teal-500 border-gray-600 rounded bg-gray-800"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300" title="Remember my username for next time">
              {t('auth.rememberMe')}
            </label>
          </div>

          <div className="text-sm">
            <button 
              type="button"
              onClick={handleForgotPassword}
              className="font-medium text-teal-400 hover:text-teal-300 transition-colors duration-200 bg-transparent border-none cursor-pointer"
            >
              {t('auth.forgotPasswordLink')}
            </button>
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-cyan-500 hover:from-teal-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 backdrop-blur-sm"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                {t('auth.signingIn')}
              </div>
            ) : (
              t('auth.login')
            )}
          </button>
        </div>
      </form>

      {/* Message Display */}
      {errorMessage && (
        <div className={`mt-4 p-4 rounded-lg border ${
          errorType === 'warning' 
            ? 'bg-yellow-900/20 border-yellow-500/50 text-yellow-200' 
            : errorType === 'success'
            ? 'bg-green-900/20 border-green-500/50 text-green-200'
            : 'bg-red-900/20 border-red-500/50 text-red-200'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {errorType === 'warning' ? (
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : errorType === 'success' ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                errorType === 'warning' ? 'text-yellow-200' : 
                errorType === 'success' ? 'text-green-200' : 'text-red-200'
              }`}>
                {errorType === 'warning' ? 'Warning' : 
                 errorType === 'success' ? 'Success' : 'Login Failed'}
              </h3>
              <div className={`mt-2 text-sm ${
                errorType === 'warning' ? 'text-yellow-300' : 
                errorType === 'success' ? 'text-green-300' : 'text-red-300'
              }`}>
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        onEmailSent={handleEmailSent}
      />

      {/* Copyright Footer */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-400">
          {t('auth.copyright')}{' '}
          <a 
            href="https://flowai.co.th" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-teal-400 hover:text-teal-300 transition-colors duration-200 font-medium"
          >
            Flow AI
          </a>
        </p>
        {accessUrl && (
          <p className="mt-1 text-xs text-gray-500">
            Access URL: <a href={accessUrl} className="underline hover:text-gray-300">{accessUrl}</a>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login; 